import React from "react";
import { Link } from "react-router-dom";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedinIn,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";

const Footer = () => {
  return (
    <footer className="relative bg-secondary-900 text-gray-300 pt-20 pb-10 overflow-hidden border-t border-secondary-800">
      {/* Decorative gradient blobs — brand magenta */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary-800 opacity-15 blur-[120px] rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-700 opacity-10 blur-[120px] rounded-full translate-x-1/2 translate-y-1/2 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-primary-700 opacity-5 blur-[80px] rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          {/* Company Info */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-3">
              {/* Logo Graphic */}
              <div className="bg-white p-3 rounded-lg inline-block">
                <img
                  src="/logo.png"
                  alt="Chapter 24 Logo"
                  className="h-16 w-auto object-contain"
                />
              </div>

              {/* Logo Text */}
              <div className="flex flex-col">
                <div className="flex items-baseline">
                  <span className="font-heading font-extrabold text-2xl text-primary-300">
                    Chapter
                  </span>
                  <span className="font-heading font-extrabold text-2xl text-accent-400 ml-0.5">
                    24
                  </span>
                </div>
                <div className="flex items-baseline text-xs font-semibold tracking-wide">
                  <span className="text-accent-400">Working</span>
                  <span className="text-primary-300 ml-1">
                    Women's Residence
                  </span>
                </div>
              </div>
            </Link>
            <p className="text-sm leading-relaxed text-gray-400">
              Your trusted partner for finding the perfect, safe, and premium PG
              accommodation. Designed exclusively for working professionals
              seeking comfort and community.
            </p>
            <div className="flex space-x-3">
              {[
                {
                  icon: FaFacebook,
                  href: "https://www.facebook.com/people/Chapter24/61587948214392/",
                  label: "Facebook",
                },
                {
                  icon: FaInstagram,
                  href: "https://www.instagram.com/chapter24_official",
                  label: "Instagram",
                },
                {
                  icon: FaLinkedinIn,
                  href: "https://www.linkedin.com/company/chapter24-official/",
                  label: "LinkedIn",
                },
              ].map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  aria-label={social.label}
                  className="w-9 h-9 rounded-full bg-secondary-800/60 border border-secondary-700 flex items-center justify-center hover:bg-primary-600 hover:border-primary-500 hover:text-white hover:shadow-glow hover:-translate-y-1 transition-all duration-300"
                >
                  <social.icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-heading font-bold text-base mb-6 relative inline-block">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-primary-500 to-transparent rounded-full" />
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'Home', path: '/' },
                { label: 'About Us', path: '/about' },
                { label: 'Properties', path: '/properties' },
                { label: 'Blogs', path: '/blogs' },
                { label: 'Testimonials', path: '/testimonials' },
              ].map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-primary-400 flex items-center gap-2.5 transition-all duration-300 hover:translate-x-1 group text-sm"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 group-hover:bg-primary-500 transition-colors flex-shrink-0" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-heading font-bold text-base mb-6 relative inline-block">
              Legal
              <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-primary-500 to-transparent rounded-full" />
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'Privacy Policy', path: '/privacy-policy' },
                { label: 'Terms & Conditions', path: '/terms' },
              ].map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-primary-400 flex items-center gap-2.5 transition-all duration-300 hover:translate-x-1 group text-sm"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 group-hover:bg-primary-500 transition-colors flex-shrink-0" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-heading font-bold text-base mb-6 relative inline-block">
              Contact Us
              <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-primary-500 to-transparent rounded-full" />
            </h4>
            <ul className="space-y-4">
              {[
                {
                  icon: FaPhone,
                  label: "Call Support",
                  value: "+91 99606 97118",
                },
                {
                  icon: FaEnvelope,
                  label: "Email Us",
                  value: "info@chapter24.co.in",
                },
              ].map(({ icon: Icon, label, value }, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="mt-0.5 w-8 h-8 rounded-lg bg-primary-900/40 border border-primary-800/50 flex flex-shrink-0 items-center justify-center text-primary-400">
                    <Icon size={13} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">
                      {label}
                    </p>
                    <p className="text-sm text-gray-300 font-medium">{value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Popular Locations - Below the Grid */}
        <div className="mb-12">
          <h4 className="text-white font-heading font-bold text-base mb-6 relative inline-block">
            Popular Locations
            <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-primary-500 to-transparent rounded-full" />
          </h4>
          <div className="flex flex-wrap gap-2">
            {[
              "Akurdi",
              "Nigdi",
              "Nigdi Pradhikaran",
              "Sambhaji Chowk",
              "Kiwale-Ravet",
              "Pimple Saudagar",
              "Talwade-Nighoje",
              "Tathawade",
            ].map((loc, idx) => (
              <Link
                key={idx}
                to={`/properties?search=${loc.toLowerCase().replace(" ", "-")}`}
                className="bg-secondary-800/50 border border-secondary-700 hover:border-primary-500 text-gray-400 hover:text-white hover:bg-primary-600/20 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 hover:-translate-y-0.5"
              >
                {loc}
              </Link>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-secondary-800/80 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} Chapter24 Pvt. Ltd. All rights reserved.
          </p>
          <p className="text-xs text-gray-500">
            Designed by{" "}
            <a
              href="https://www.chapter24.co.in"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 font-semibold transition-colors"
            >
              Chapter24
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
